import ContentCopy from "@mui/icons-material/ContentCopy";
import Telegram from "@mui/icons-material/Telegram";
import WhatsApp from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface ReferralCodeCardProps {
  referralCode: string;
  referralLink: string;
  discountValue: number;
  discountType: string;
  promotionalText?: string;
}

const ReferralCodeCard: React.FC<ReferralCodeCardProps> = ({
  referralCode,
  referralLink,
  discountValue,
  discountType,
  promotionalText,
}) => {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setSnackbarMessage(t("profile.referral.codeCopied"));
      setSnackbarOpen(true);
    } catch {
      // clipboard not available, silently ignore
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setSnackbarMessage(t("profile.referral.linkCopied"));
      setSnackbarOpen(true);
    } catch {
      // clipboard not available, silently ignore
    }
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(
      `${t("profile.referral.shareText")} ${referralLink}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const handleShareTelegram = () => {
    const url = encodeURIComponent(referralLink);
    const text = encodeURIComponent(t("profile.referral.shareText"));
    window.open(
      `https://t.me/share/url?url=${url}&text=${text}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const discountLabel =
    discountType === "Percentage" ? `${discountValue}%` : `$${discountValue}`;

  return (
    <>
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t("profile.referral.yourReferralCode")}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {promotionalText ||
              t("profile.referral.defaultPromoText", {
                discount: discountLabel,
              })}
          </Typography>

          {/* Referral Code Display */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
            }}
          >
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#f3f4f6",
                border: "2px dashed #2563eb",
                borderRadius: 2,
                py: 2,
                px: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color="primary"
                letterSpacing={3}
                className="notranslate"
                translate="no"
                sx={{ fontFamily: "monospace" }}
              >
                {referralCode}
              </Typography>
            </Box>
            <Tooltip title={t("profile.referral.copyCode")}>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={handleCopyCode}
                sx={{ whiteSpace: "nowrap", py: 1.5 }}
              >
                {t("profile.referral.copyCode")}
              </Button>
            </Tooltip>
          </Box>

          {/* Referral Link */}
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            sx={{ mt: 2 }}
          >
            {t("profile.referral.referralLink")}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <TextField
              value={referralLink}
              size="small"
              fullWidth
              inputProps={{ readOnly: true, className: "notranslate" }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.85rem",
                  color: "#374151",
                },
              }}
            />
            <Tooltip title={t("profile.referral.copyLink")}>
              <IconButton
                onClick={handleCopyLink}
                color="primary"
                sx={{ border: "1px solid", borderColor: "primary.main" }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Share Buttons */}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t("profile.referral.shareVia")}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={handleShareWhatsApp}
              sx={{
                borderColor: "#25d366",
                color: "#25d366",
                "&:hover": {
                  borderColor: "#1da851",
                  backgroundColor: "#f0fdf4",
                },
              }}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              startIcon={<Telegram />}
              onClick={handleShareTelegram}
              sx={{
                borderColor: "#229ed9",
                color: "#229ed9",
                "&:hover": {
                  borderColor: "#1a7fa8",
                  backgroundColor: "#f0f9ff",
                },
              }}
            >
              Telegram
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default ReferralCodeCard;
